// Backlog right-side rail. Compact list of parked projects grouped by
// priority, with Department / Priority dropdown filters built to absorb
// more filter dimensions as the schema grows (Owner, tags, etc.).

import { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useHistory } from 'react-router-dom';

import {
  PROJECT_PRIORITY_COLORS,
  PROJECT_PRIORITY_LABELS,
  PROJECT_PRIORITY_ORDER,
} from '../../pm/constants';
import { updateProject } from '../../pm/firebase';
import { useProjectDepartments } from '../../pm/useProjectDepartments';
import { Project, ProjectDepartment, ProjectPriority, Task } from '../../pm/types';
import { IconChevDown, IconClose, IconFilter, IconPlus } from './Icons';

interface Filters {
  department: 'all' | ProjectDepartment;
  priority: 'all' | ProjectPriority;
}

interface Props {
  open: boolean;
  onClose: () => void;
  projects: Project[];   // all projects; we filter to backlogged inside
  tasks: Task[];         // for counts on each row
  /** Notifies the parent that a rail row is being dragged so Grid lanes can
   *  light up as drop targets. Optional — the rail still functions without
   *  cross-component DnD wired in. */
  onProjectDragStart?: (id: string) => void;
  onProjectDragEnd?: () => void;
}

const BacklogRail: React.FC<Props> = ({
  open,
  onClose,
  projects,
  tasks,
  onProjectDragStart,
  onProjectDragEnd,
}) => {
  const history = useHistory();
  const { departments, getDept } = useProjectDepartments();
  const [filters, setFilters] = useState<Filters>({ department: 'all', priority: 'all' });
  const [menuOpen, setMenuOpen] = useState<'department' | 'priority' | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Click-away dismisses the filter menu without taking focus from the rail.
  // We attach on document so it works even when the click lands on the
  // rail backdrop. Re-installs each time menuOpen flips so we don't trap
  // a stale closure.
  useMemo(() => {
    if (!menuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(null);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);

  // Backlog pool: explicitly backlogged projects PLUS unprioritized ones.
  // Unprioritized projects don't have a Grid lane (we removed the bottom
  // catch-all per design), so the Backlog rail is where they get surfaced.
  // Done projects are excluded — they live in the Done rail, never both,
  // even when they're also unprioritized.
  const backlog = useMemo(
    () => projects.filter((p) => !p.done && (p.backlogged || !p.priority)),
    [projects],
  );
  const filtered = useMemo(() => {
    return backlog.filter((p) => {
      if (filters.department !== 'all' && p.department !== filters.department) return false;
      if (filters.priority !== 'all' && p.priority !== filters.priority) return false;
      return true;
    });
  }, [backlog, filters]);

  // Per-project task counts, computed once per render of the rail.
  const taskCounts = useMemo(() => {
    const m: Record<string, number> = {};
    for (const t of tasks) {
      m[t.projectId] = (m[t.projectId] || 0) + 1;
    }
    return m;
  }, [tasks]);

  // Group filtered backlog by priority for a quick visual scan.
  const groups = useMemo(() => {
    return PROJECT_PRIORITY_ORDER
      .map((id) => ({
        id,
        label: PROJECT_PRIORITY_LABELS[id],
        color: PROJECT_PRIORITY_COLORS[id],
        items: filtered.filter((p) => p.priority === id),
      }))
      .filter((g) => g.items.length > 0);
  }, [filtered]);

  const unprioritizedItems = filtered.filter((p) => !p.priority);
  const activeFilterCount =
    (filters.department !== 'all' ? 1 : 0) + (filters.priority !== 'all' ? 1 : 0);

  function deptLabel(): string {
    if (filters.department === 'all') return 'All departments';
    return getDept(filters.department as ProjectDepartment)?.label ?? 'Department';
  }
  function priLabel(): string {
    if (filters.priority === 'all') return 'All priorities';
    return PROJECT_PRIORITY_LABELS[filters.priority as ProjectPriority];
  }

  async function activate(projectId: string) {
    try {
      await updateProject(projectId, { backlogged: null });
    } catch {
      // Failures surface through the realtime subscription; nothing else to
      // do here, the row stays parked and the user can retry.
    }
  }

  // Portal to document.body so the rail escapes IonRouterOutlet's stacking
  // context. Without this, the notification bell (rendered outside the
  // outlet) sits in a higher outer stacking context and floats above the
  // rail's close button regardless of z-index inside the outlet.
  //
  // The wrapping .pm-app div is critical: all our --rr-* / --pm-* tokens
  // are scoped to .pm-app, so a portal direct to body would render with
  // every var(--...) resolving to its initial value — making the rail
  // background literally transparent. Wrapping reinstates the scope.
  return createPortal(
    <div className="pm-app pm-bl-portal">
    <aside className={`pm-bl-rail${open ? ' is-open' : ''}`} aria-hidden={!open}>
      <div className="pm-bl-rail-hd">
        <div>
          <div className="pm-bl-rail-eyebrow">Backlog</div>
          <div className="pm-bl-rail-count">
            {backlog.length} project{backlog.length === 1 ? '' : 's'} parked
          </div>
        </div>
        <button
          type="button"
          className="pm-bl-rail-close"
          onClick={onClose}
          aria-label="Close backlog"
        >
          <IconClose size={16} />
        </button>
      </div>

      <div className="pm-bl-rail-filters" ref={menuRef}>
        <div className="pm-bl-rail-filter-wrap">
          <button
            type="button"
            className={`pm-bl-rail-filter-btn${filters.department !== 'all' ? ' is-on' : ''}`}
            onClick={() => setMenuOpen((v) => (v === 'department' ? null : 'department'))}
          >
            <IconFilter size={11} />
            <span>{deptLabel()}</span>
            <IconChevDown size={11} />
          </button>
          {menuOpen === 'department' ? (
            <div className="pm-bl-rail-menu">
              <FilterItem
                checked={filters.department === 'all'}
                onClick={() => { setFilters((f) => ({ ...f, department: 'all' })); setMenuOpen(null); }}
              >
                <span>All departments</span>
                <span className="ct">{backlog.length}</span>
              </FilterItem>
              <div className="pm-bl-rail-menu-sep" />
              {departments.map((d) => {
                const ct = backlog.filter((p) => p.department === d.id).length;
                return (
                  <FilterItem
                    key={d.id}
                    checked={filters.department === d.id}
                    onClick={() => { setFilters((f) => ({ ...f, department: d.id })); setMenuOpen(null); }}
                    disabled={ct === 0}
                  >
                    <span className="pm-pri-dot" style={{ background: d.swatch }} />
                    <span>{d.label}</span>
                    <span className="ct">{ct}</span>
                  </FilterItem>
                );
              })}
            </div>
          ) : null}
        </div>

        <div className="pm-bl-rail-filter-wrap">
          <button
            type="button"
            className={`pm-bl-rail-filter-btn${filters.priority !== 'all' ? ' is-on' : ''}`}
            onClick={() => setMenuOpen((v) => (v === 'priority' ? null : 'priority'))}
          >
            <span>{priLabel()}</span>
            <IconChevDown size={11} />
          </button>
          {menuOpen === 'priority' ? (
            <div className="pm-bl-rail-menu">
              <FilterItem
                checked={filters.priority === 'all'}
                onClick={() => { setFilters((f) => ({ ...f, priority: 'all' })); setMenuOpen(null); }}
              >
                <span>All priorities</span>
                <span className="ct">{backlog.length}</span>
              </FilterItem>
              <div className="pm-bl-rail-menu-sep" />
              {PROJECT_PRIORITY_ORDER.map((p) => {
                const ct = backlog.filter((b) => b.priority === p).length;
                return (
                  <FilterItem
                    key={p}
                    checked={filters.priority === p}
                    onClick={() => { setFilters((f) => ({ ...f, priority: p })); setMenuOpen(null); }}
                    disabled={ct === 0}
                  >
                    <span className="pm-pri-dot" style={{ background: PROJECT_PRIORITY_COLORS[p] }} />
                    <span>{PROJECT_PRIORITY_LABELS[p]}</span>
                    <span className="ct">{ct}</span>
                  </FilterItem>
                );
              })}
            </div>
          ) : null}
        </div>

        {activeFilterCount > 0 ? (
          <button
            type="button"
            className="pm-bl-rail-filter-clear"
            onClick={() => setFilters({ department: 'all', priority: 'all' })}
          >
            Clear
          </button>
        ) : null}
      </div>

      <div className="pm-bl-rail-body">
        {groups.length === 0 && unprioritizedItems.length === 0 ? (
          <div className="pm-bl-rail-empty">
            <div className="pm-bl-rail-empty-hd">
              {backlog.length === 0 ? 'Nothing here' : 'No matches'}
            </div>
            <div className="pm-bl-rail-empty-sub">
              {backlog.length === 0
                ? 'Use "Move to backlog" on a project to park it here.'
                : 'Try clearing your filters.'}
            </div>
          </div>
        ) : null}

        {groups.map((g) => (
          <div key={g.id} className="pm-bl-rail-section">
            <div className="pm-bl-rail-sec-hd">
              <span className="pm-pri-dot" style={{ background: g.color }} />
              <span>{g.label}</span>
              <span className="pm-bl-rail-sec-ct">{g.items.length}</span>
            </div>
            {g.items.map((p) => (
              <BacklogRow
                key={p.id}
                project={p}
                taskCount={taskCounts[p.id] || 0}
                onOpen={() => history.push(`/Projects/${p.id}`)}
                onActivate={() => activate(p.id)}
                onDragStart={onProjectDragStart}
                onDragEnd={onProjectDragEnd}
                getDept={getDept}
              />
            ))}
          </div>
        ))}

        {unprioritizedItems.length > 0 ? (
          <div className="pm-bl-rail-section">
            <div className="pm-bl-rail-sec-hd">
              <span className="pm-pri-dot" style={{ background: 'var(--rr-stone-300)' }} />
              <span>Unprioritized</span>
              <span className="pm-bl-rail-sec-ct">{unprioritizedItems.length}</span>
            </div>
            {unprioritizedItems.map((p) => (
              <BacklogRow
                key={p.id}
                project={p}
                taskCount={taskCounts[p.id] || 0}
                onOpen={() => history.push(`/Projects/${p.id}`)}
                onActivate={() => activate(p.id)}
                onDragStart={onProjectDragStart}
                onDragEnd={onProjectDragEnd}
                getDept={getDept}
              />
            ))}
          </div>
        ) : null}
      </div>

      <div className="pm-bl-rail-foot">
        <span className="pm-bl-rail-foot-hint">
          <IconPlus size={12} /> Move active projects here from their detail page.
        </span>
      </div>
    </aside>
    </div>,
    document.body,
  );
};

interface RowProps {
  project: Project;
  taskCount: number;
  onOpen: () => void;
  onActivate: () => void;
  /** When set, the row becomes a drag source. The parent uses this to
   *  light up Grid drop targets and to commit the priority change on drop. */
  onDragStart?: (id: string) => void;
  onDragEnd?: () => void;
  /** Department lookup passed down from the parent so the row doesn't have
   *  to subscribe to RTDB on its own (which would N-fold the subscriptions
   *  in a busy rail). */
  getDept: (id: string | undefined) => { label: string; swatch: string } | undefined;
}

const BacklogRow: React.FC<RowProps> = ({
  project,
  taskCount,
  onOpen,
  onActivate,
  onDragStart,
  onDragEnd,
  getDept,
}) => {
  const dept = getDept(project.department);
  return (
    <div
      className="pm-bl-rail-item"
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } }}
      draggable={!!onDragStart}
      onDragStart={(e) => {
        if (!onDragStart) return;
        if (e.dataTransfer) {
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', project.id);
        }
        onDragStart(project.id);
      }}
      onDragEnd={onDragEnd}
    >
      <div className="pm-bl-rail-item-main">
        <div className="pm-bl-rail-item-name">{project.title}</div>
        <div className="pm-bl-rail-item-meta">
          {dept ? (
            <>
              <span className="pm-pri-dot" style={{ background: dept.swatch, width: 6, height: 6 }} />
              <span>{dept.label}</span>
              <span aria-hidden>·</span>
            </>
          ) : null}
          <span>{taskCount} task{taskCount === 1 ? '' : 's'}</span>
        </div>
      </div>
      <button
        type="button"
        className="pm-bl-rail-promote"
        onClick={(e) => { e.stopPropagation(); onActivate(); }}
        title="Activate — move out of backlog"
      >
        Activate
      </button>
    </div>
  );
};

interface FilterItemProps {
  checked: boolean;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}

const FilterItem: React.FC<FilterItemProps> = ({ checked, onClick, disabled, children }) => {
  return (
    <button
      type="button"
      className={`pm-bl-rail-menu-item${checked ? ' is-on' : ''}${disabled ? ' is-disabled' : ''}`}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
    >
      <span className="pm-bl-rail-menu-check">
        {checked ? (
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="3 8.5 6.5 12 13 4.5" />
          </svg>
        ) : null}
      </span>
      <span className="pm-bl-rail-menu-label">{children}</span>
    </button>
  );
};

export default BacklogRail;
